import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { getRequirements } from "sr/utils/api/corporate/requirements";
import { getBiddings } from "sr/utils/api/corporate/biddings";
import ContentWithLoader from "sr/layout/common/ContentLoader";
import { Pagination } from "app/pages/component/Pagination";
import { Empty } from "../candidates/component/Empty";
import { MainWrapper } from "./components/MainWrapper";
import { ListingHeader } from "sr/layout/common/ListingHeader";
import BiddingsList from "./BiddingsList";
import { MetaBox } from 'sr/helpers/ui-components/MetaBox'
import { StatusPill } from 'sr/helpers/ui-components/StatusPill'
import { getDuration } from 'sr/utils/api/masterData/getDuration'

type Requirement = {
  id: string;
  title: string;
  description: string;
  region?: string;
  budget?: string;
  status: string;
  updatedAt?: string;
  address?: {
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    state?: string;
    country?: string;
    postal?: string;
  };
};

type Bidding = {
  id: string;
  corporate_id: { _id: string; corporate_name: string };
  requirement_id: { _id: string; title: string; end_date?: string };
  description: string; 
  pricing_type: "Fixed" | "Hourly";
  bid_amount: string;
  status: string;
  createdAt?: string;
};

export const ProjectsListing: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { user_id } = getUserDetails();

  const [tab, setTab] = useState<"requirements" | "biddings">("requirements");
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [projects, setProjects] = useState<Bidding[]>([]);
  const [loader, setLoader] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPage, setTotalPage] = useState(1);

  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [corpMap, setCorpMap] = useState<Record<string, any>>({});
  const [durationLabelMap, setDurationLabelMap] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadDuration = async () => {
      try {
        const d = await getDuration();
        if (d?.success && Array.isArray(d.data)) {
          const m: Record<string, string> = {};
          d.data.forEach((i: any) => (m[i.value] = i.label));
          setDurationLabelMap(m);
        }
      } catch {}
    };
    loadDuration();
  }, []);

  const fetchRequirements = async () => {
    setLoader(true);
    try {
      const res = await getRequirements({ page, limit, status: "open" });
      if (res?.success) {
        setRequirements(res.data || []);
      }
    } finally {
      setLoader(false);
    }
  };

  const fetchProjects = async () => {
    setProjectsLoaded(false);
    try {
      const res = await getBiddings({ page, limit, company_id: user_id });
      if (res?.success) {
        setProjects(res.data || []);
      }
    } finally {
      setProjectsLoaded(true);
    }
  };

  useEffect(() => {
    if (tab === "requirements") fetchRequirements();
  }, [tab, page, limit]);

  useEffect(() => {
    if (user_id) fetchProjects();
  }, [user_id]);

  const filteredRequirements = useMemo(() => {
    const q = (search || '').toLowerCase();
    let list = requirements.filter((r) => {
      if (!q) return true;
      const title = (r.title || '').toLowerCase();
      const addr = (
        (r.address?.address_line_1 || '') + ' ' +
        (r.address?.city || '') + ' ' +
        (r.address?.state || '') + ' ' +
        (r.address?.country || '')
      ).toLowerCase();
      return title.includes(q) || addr.includes(q);
    });

    if (sortBy === 'title') list = list.sort((a, b) => a.title.localeCompare(b.title));
    else if (sortBy === '-title') list = list.sort((a, b) => b.title.localeCompare(a.title));
    else if (sortBy === 'date') list = list.sort((a, b) => new Date(a.updatedAt || 0).getTime() - new Date(b.updatedAt || 0).getTime());
    else if (sortBy === '-date') list = list.sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
    return list;
  }, [requirements, search, sortBy]);

  const filteredProjects = useMemo(() => {
    const q = (search || '').toLowerCase();
    let list = projects.filter((b: any) => {
      if (!q) return true;
      const title = (b?.requirement_id?.title || '').toLowerCase();
      const status = String(b.status || '').toLowerCase();
      const amount = String(b.bid_amount || '').toLowerCase();
      const corp = (b?.corporate_id?.corporate_name || '').toLowerCase();
      return title.includes(q) || status.includes(q) || amount.includes(q) || corp.includes(q);
    });
    if (sortBy === 'amount') list = list.sort((a: any, b: any) => Number(a.bid_amount || 0) - Number(b.bid_amount || 0));
    else if (sortBy === '-amount') list = list.sort((a: any, b: any) => Number(b.bid_amount || 0) - Number(a.bid_amount || 0));
    else if (sortBy === 'date') list = list.sort((a: any, b: any) => new Date(a.updatedAt || a.createdAt || 0).getTime() - new Date(b.updatedAt || b.createdAt || 0).getTime());
    else if (sortBy === '-date') list = list.sort((a: any, b: any) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime());
    return list;
  }, [projects, search, sortBy]);

  const totalItems = tab === 'requirements' ? filteredRequirements.length : filteredProjects.length;
  const computedTotalPage = Math.max(1, Math.ceil(totalItems / limit));
  const start = (page - 1) * limit;
  const pagedRequirements = filteredRequirements.slice(start, start + limit);
  const pagedProjects = filteredProjects.slice(start, start + limit);

  useEffect(() => {
    setTotalPage(computedTotalPage);
  }, [computedTotalPage]);

  const formatAddress = (req: Requirement) => {
    if (!req?.address) return "—";
    const { address_line_1, address_line_2, city, state, country, postal } =
      req.address;
    return [address_line_1, address_line_2, city, state, country, postal]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <MainWrapper>
      <div className="py-3">
        <ListingHeader
          title={<FormattedMessage id="RECRUITER.PROJECTS.TITLE" defaultMessage="Projects" />}
          tabs={[
            { key: 'requirements', label: <FormattedMessage id="RECRUITER.PROJECTS.TAB.REQUIREMENTS" defaultMessage={`Requirements (${requirements.length})`} /> },
            { key: 'biddings', label: <FormattedMessage id="RECRUITER.PROJECTS.TAB.BIDDINGS" defaultMessage={`My Biddings (${projects.length})`} /> },
          ]}
          activeKey={tab}
          onTabChange={(k) => setTab(k as any)}
          search={search}
          onSearchChange={setSearch}
          sortBy={sortBy}
          onSortChange={setSortBy}
          primaryButton={null}
          searchPlaceholderId="SEARCH.PROJECTS.PLACEHOLDER"
        />

        {tab === "requirements" && (
          <ContentWithLoader
            loading={loader}
            content={
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-6 flex flex-col rounded-[20px] mb-6 border animate-pulse">
                    <div className="flex justify-between items-center mb-3">
                      <div className="h-5 w-40 bg-gray-200 rounded" />
                      <div className="h-4 w-16 bg-gray-200 rounded-full" />
                    </div>
                    <div className="h-3 w-3/4 bg-gray-200 rounded mb-3" />
                    <div className="flex gap-3">
                      <div className="h-10 w-40 bg-gray-200 rounded" />
                      <div className="h-10 w-28 bg-gray-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            }
          >
            {pagedRequirements.length > 0 ? (
              <>
                {pagedRequirements.map((req) => (
                  <Link
                    to={`/projects/requirement-details/${req.id}`}
                    key={req.id}
                    className="group p-6 flex flex-col rounded-[20px] mb-6 border hover:shadow hover:bg-gray-50 transition"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex flex-col gap-1">
                        <h3 className="text-lg font-semibold text-[#0078ae] group-hover:underline">
                          {req.title}
                        </h3>
                        {(() => {
                          const corpId = (req as any)?.corporate_id?._id || '';
                          const corpName = corpId ? (corpMap[corpId]?.corporate_name || corpMap[corpId]?.company_name) : '';
                          if (!corpName) return null;
                          return (
                            <div className="text-xs text-gray-600 flex items-center gap-1">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="w-4 h-4 text-gray-500"
                              >
                                <path d="M4 3a2 2 0 00-2 2v11h2v-2h12v2h2V8l-6-5H4zm8 1.5L17.5 8H12V4.5zM6 9h2v2H6V9zm0 3h2v2H6v-2zm4-3h2v2h-2V9zm0 3h2v2h-2v-2z" />
                              </svg>
                              <span className="font-medium">{corpName}</span>
                            </div>
                          );
                        })()}
                      </div>
                      <StatusPill status={req.status} />
                    </div>

                    <p className="text-sm text-gray-600 mb-4">{req.description}</p>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      <MetaBox label="Region" value={formatAddress(req)} />
                      <MetaBox label="Area" value={(req as any).area || "—"} />
                      <MetaBox label="Duration" value={(req as any).duration ? intl.formatMessage({ id: durationLabelMap[String((req as any).duration)] || String((req as any).duration), defaultMessage: String((req as any).duration) }) : "—"} />
                      <MetaBox label="Budget" value={req.budget || "—"} />
                    </div>
                  </Link>
                ))}

                <Pagination page={page} totalPage={totalPage} setPage={setPage} />
              </>
            ) : (
              !loader && <Empty text="No requirements available" />
            )}
          </ContentWithLoader>
        )}

        {tab === "biddings" && (
          <BiddingsList
            projects={pagedProjects}
            loader={loader || !projectsLoaded}
            page={page}
            totalPage={totalPage}
            setPage={setPage}
          />
        )}
      </div>
    </MainWrapper>
  );
};

export default ProjectsListing;
